/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import { useParams } from 'react-router-dom';
import useFetchList from 'hooks/useFetchList';
import { GET_ORDER_COLUMNS } from 'components/Orders/constants';
import { getExpectedDeliveryDate } from 'helpers/localize';
import { useSelector } from 'react-redux';
import { NULL_QUERY_DELAY } from '../../Shared/constants';
import { convertDataFromApi, useGenerateColumns } from '../../Shared/Table/helpers';
import OrdersTable from './widgets/OrdersTable';
import OrdersRemoveDialog from './widgets/OrdersRemoveDialog';
import { TABLE_KEYS } from '../../../redux_store/reducer/reducers/tableReducer/tableReducer';

/*
  could be rendered in 2 cases:
  - all account orders
  - orders for a special user (patientUuid in URL params)
*/

const OrdersList = forwardRef(({
  setTotalCount,
  setFilterStyle,
  tableTitle,
  q,
  hideMenuActions,
  columnList,
}, ref) => {
  const { accountUuid: paramAccountUuid } = useParams();
  const { metadata: { currentAccount } } = useSelector((state) => state.user);
  const [isOpenRemoveDialog, setOpenRemoveDialog] = useState(false);
  const [removingOrder, setRemovingOrder] = useState({});
  const [modifiedOrders, setModifiedOrders] = useState();
  const [prevTotalCount, setPrevTotalCount] = useState();
  const { isDistributionSponsor } = useSelector((state) => state.user);
  const tableKey = isDistributionSponsor ? TABLE_KEYS.ordersDSTable : TABLE_KEYS.ordersTable;
  const columns = useGenerateColumns(columnList || GET_ORDER_COLUMNS, tableKey, {}, {}).columns;
  const fields = columns.map((columnData) => columnData.accessorKey).filter(item => item !== 'expectedDeliveryDate').join();

  const baseEndpoint = `/accounts/${paramAccountUuid || currentAccount.uuid
    }/orders`;
  const {
    items,
    totalCount,
    exportItems,
    fetchExportItems,
    exportItemsLoadingStatus,
    clearFilter,
    reloadData,
    onLoadMoreRows,
    sort,
    onSort,
    filter,
    onFilter,
    loading: dataLoading,
  } = useFetchList({
    baseEndpoint,
    baseParams: {
      fields: fields,
      q: q || undefined,
    },
    exportEndpoint: `${baseEndpoint}/csv`,
    tableKey,
    canLoad: fields.length,
  });

  useEffect(() => {
    if (items) {
      const newModifiedItems = convertDataFromApi(items, tableKey, filter, {
        expectedDeliveryDate: (order) => getExpectedDeliveryDate(order.orderDate),
      });
      setModifiedOrders(newModifiedItems);
    }
  }, [items]);

  useEffect(() => {
    if (prevTotalCount !== totalCount) {
      setFilterStyle(!!filter?.reduce((accumulator, { value }) => {
        return accumulator + value.toString().replaceAll(' ', '').length;
      }, 0));
      setTotalCount && setTotalCount(totalCount);
      setPrevTotalCount(totalCount);
    }
  }, [filter, totalCount]);

  const reloadOrdersList = (hasFilter, delay) => {
    if (!hasFilter) {
      clearFilter();
    } else {
      reloadData({ delay: delay || NULL_QUERY_DELAY });
    }
  };

  useImperativeHandle(ref, () => ({
    reloadList(v) {
      reloadOrdersList(v);
    },
  }));

  return <>
    <OrdersTable
      modifiedOrders={modifiedOrders}
      setOpenRemoveDialog={setOpenRemoveDialog}
      setRemovingOrder={setRemovingOrder}
      dataLoading={dataLoading}
      totalCount={totalCount}
      onLoadMoreRows={onLoadMoreRows}
      columns={columns}
      tableKey={tableKey}
      sort={sort}
      onSort={onSort}
      filter={filter}
      tableTitle={tableTitle}
      onFilter={onFilter}
      reloadOrdersList={reloadOrdersList}
      exportItems={exportItems}
      exportItemsLoadingStatus={exportItemsLoadingStatus}
      fetchExportItems={fetchExportItems}
      hideMenuActions={hideMenuActions}
    />
    <OrdersRemoveDialog
      reloadData={reloadData}
      removingOrder={removingOrder}
      setOpenRemoveDialog={setOpenRemoveDialog}
      setRemovingOrder={setRemovingOrder}
      isOpenRemoveDialog={isOpenRemoveDialog}
    />
  </>;
});

export default OrdersList;
